import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  limit,
  runTransaction,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { registrarAsientoCore } from "./accountingCore";
import { resolverCuentasContables } from "./accountingEngine";

const db = getFirestore(firebaseApp);

const cleanText = (v) => (v == null ? "" : String(v).trim());
const cleanNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const DEFAULT_RULES = {
  COSTO_DIRECTO: { debe: "601101", haber: "421201", destino: "941101" },
  GASTO_ADMINISTRATIVO: { debe: "631101", haber: "421201", destino: "951101" },
  GASTO_VENTAS: { debe: "641101", haber: "421201", destino: "951201" },
  ACTIVO_FIJO: { debe: "331101", haber: "421201", destino: "" },
};
const DESTINO_CUENTAS = {
  OPERATIVO: "941101",
  PRODUCCION: "941101",
  ADMINISTRATIVO: "951101",
  VENTAS: "951201",
};

const getReglaTipoGasto = async (rucEmpresa, tipoGasto) => {
  const q = query(
    collection(db, CONT_COLLECTIONS.REGLAS_ASIENTO),
    where("ruc_empresa", "==", cleanText(rucEmpresa)),
    where("tipo_gasto", "==", cleanText(tipoGasto)),
    where("estado", "==", "ACTIVO"),
    limit(1)
  );
  const snap = await getDocs(q);
  if (!snap.empty) {
    return snap.docs[0].data();
  }
  const fallback = DEFAULT_RULES[cleanText(tipoGasto)] || DEFAULT_RULES.GASTO_ADMINISTRATIVO;
  return {
    cuenta_debe: fallback.debe,
    cuenta_haber: fallback.haber,
    cuenta_destino: fallback.destino,
  };
};

const createAsientoPayload = ({
  rucEmpresa,
  origen,
  origenId,
  fecha,
  glosa,
  moneda,
  tipoCambio,
  debe,
  haber,
}) => {
  const totalDebe = debe.reduce((acc, x) => acc + cleanNumber(x.monto), 0);
  const totalHaber = haber.reduce((acc, x) => acc + cleanNumber(x.monto), 0);
  return {
    ruc_empresa: cleanText(rucEmpresa),
    origen: cleanText(origen),
    origen_id: cleanText(origenId),
    fecha: cleanText(fecha),
    glosa: cleanText(glosa),
    moneda: cleanText(moneda || "PEN"),
    tipo_cambio: cleanNumber(tipoCambio || 1),
    debe,
    haber,
    total_debe: Number(totalDebe.toFixed(2)),
    total_haber: Number(totalHaber.toFixed(2)),
    estado: "REGISTRADO",
    updated_at: serverTimestamp(),
    created_at: serverTimestamp(),
  };
};

export const registrarAsientoCompra = async ({
  rucEmpresa,
  compraId,
  fecha,
  proveedorRuc,
  proveedorRazonSocial,
  tipoGasto,
  total,
  moneda,
  tipoCambio,
  idempotencyKey,
  metadata = {},
}, transaction) => {
  const cfg = await resolverCuentasContables(rucEmpresa);
  const regla = await getReglaTipoGasto(rucEmpresa, tipoGasto);
  const monto = cleanNumber(total);
  const asientoId = `${cleanText(compraId)}_COMPRA`;
  const payload = createAsientoPayload({
    rucEmpresa,
    origen: "COMPRA",
    origenId: compraId,
    fecha,
    glosa: `Compra ${cleanText(proveedorRazonSocial)} (${cleanText(proveedorRuc)})`,
    moneda,
    tipoCambio,
    debe: [{ cuenta: cleanText(regla.cuenta_debe), descripcion: `Gasto por ${cleanText(tipoGasto)}`, monto }],
    haber: [{ cuenta: cfg.cuenta_proveedores, descripcion: "Proveedores por pagar", monto }],
  });

  const key = idempotencyKey || `${cleanText(rucEmpresa)}_COMPRA_${cleanText(compraId)}_v1`;
  const auditMetadata = {
    sourceModule: "GESTION_COMPRAS",
    sourceType: "COMPRA",
    sourceId: compraId,
    sourceDocumentId: compraId,
    ...metadata,
  };

  if (!transaction) {
    throw new Error("Transacción requerida: registrarAsientoCompra debe ejecutarse dentro de una transacción de Firestore.");
  }
  await registrarAsientoCore(transaction, {
    rucEmpresa,
    asientoId,
    payload,
    idempotencyKey: key,
    metadata: auditMetadata,
  });
  return asientoId;
};

export const registrarAsientoPagoProveedor = async ({
  rucEmpresa,
  pagoId,
  cuentaPagarId,
  proveedorRazonSocial,
  monto,
  fechaPago,
  moneda,
  tipoCambio,
  metodoPago,
  idempotencyKey,
  metadata = {},
}, transaction) => {
  const cfg = await resolverCuentasContables(rucEmpresa);
  const m = cleanNumber(monto);
  const medPagoUpper = String(metodoPago || "").toUpperCase();
  const esPagoSocio = medPagoUpper === "PAGO PERSONAL (SOCIO)" || medPagoUpper === "PAGO PERSONAL" || medPagoUpper === "SOCIO";

  const cuentaHaber = esPagoSocio ? cfg.cuenta_retiro_socio : cfg.cuenta_bancos;
  const descHaber = esPagoSocio ? "Cuentas por cobrar a socios - Retiro de socio" : "Bancos cuenta operativa";

  const asientoId = `${cleanText(pagoId)}_PAGO_PROVEEDOR`;
  const payload = createAsientoPayload({
    rucEmpresa,
    origen: "PAGO_PROVEEDOR",
    origenId: cuentaPagarId,
    fecha: fechaPago,
    glosa: `Pago proveedor ${cleanText(proveedorRazonSocial)}`,
    moneda,
    tipoCambio,
    debe: [{ cuenta: cfg.cuenta_proveedores, descripcion: "Proveedores por pagar", monto: m }],
    haber: [{ cuenta: cuentaHaber, descripcion: descHaber, monto: m }],
  });

  const key = idempotencyKey || `${cleanText(rucEmpresa)}_PAGO_PROVEEDOR_${cleanText(pagoId)}_v1`;
  const auditMetadata = {
    sourceModule: "GESTION_COMPRAS",
    sourceType: "PAGO_PROVEEDOR",
    sourceId: pagoId,
    sourceDocumentId: cuentaPagarId,
    ...metadata,
  };

  if (!transaction) {
    throw new Error("Transacción requerida: registrarAsientoPagoProveedor debe ejecutarse dentro de una transacción de Firestore.");
  }
  await registrarAsientoCore(transaction, {
    rucEmpresa,
    asientoId,
    payload,
    idempotencyKey: key,
    metadata: auditMetadata,
  });
  return asientoId;
};

export const registrarAsientoDiferenciaCambio = async ({
  rucEmpresa,
  pagoId,
  cuentaPagarId,
  proveedorRazonSocial,
  diferenciaPen,
  fecha,
  idempotencyKey,
  metadata = {},
}, transaction) => {
  const cfg = await resolverCuentasContables(rucEmpresa);
  const dif = cleanNumber(diferenciaPen);
  if (Math.abs(dif) < 0.01) return null;

  const perdita = dif > 0;
  const monto = Math.abs(dif);
  const asientoId = `${cleanText(pagoId)}_DIF_CAMBIO`;
  const payload = createAsientoPayload({
    rucEmpresa,
    origen: "DIFERENCIA_CAMBIO",
    origenId: cuentaPagarId,
    fecha,
    glosa: `Diferencia de cambio pago ${cleanText(proveedorRazonSocial)}`,
    moneda: "PEN",
    tipoCambio: 1,
    debe: [
      {
        cuenta: perdita ? cfg.cuenta_perdida_cambio : cfg.cuenta_proveedores,
        descripcion: perdita ? "Pérdida por diferencia de cambio" : "Proveedores por pagar",
        monto,
      },
    ],
    haber: [
      {
        cuenta: perdita ? cfg.cuenta_proveedores : cfg.cuenta_ganancia_cambio,
        descripcion: perdita ? "Proveedores por pagar" : "Ganancia por diferencia de cambio",
        monto,
      },
    ],
  });

  const key = idempotencyKey || `${cleanText(rucEmpresa)}_DIF_CAMBIO_${cleanText(pagoId)}_v1`;
  const auditMetadata = {
    sourceModule: "GESTION_COMPRAS",
    sourceType: "DIFERENCIA_CAMBIO",
    sourceId: pagoId,
    sourceDocumentId: cuentaPagarId,
    ...metadata,
  };

  if (!transaction) {
    throw new Error("Transacción requerida: registrarAsientoDiferenciaCambio debe ejecutarse dentro de una transacción de Firestore.");
  }
  await registrarAsientoCore(transaction, {
    rucEmpresa,
    asientoId,
    payload,
    idempotencyKey: key,
    metadata: auditMetadata,
  });
  return asientoId;
};

export const registrarAsientoTesoreria = async ({
  rucEmpresa,
  movimientoId,
  tipo,
  fecha,
  tercero,
  descripcion,
  categoria,
  medioPago,
  monto,
  moneda,
  tipoCambio,
}, transaction) => {
  const m = cleanNumber(monto);
  const tipoMov = cleanText(tipo).toUpperCase();
  if (m <= 0) throw new Error("Monto inválido para asiento de tesorería.");
  if (!["PAGO", "COBRO"].includes(tipoMov)) throw new Error("Tipo inválido para asiento de tesorería.");

  const esCobro = tipoMov === "COBRO";
  const asientoId = `${cleanText(movimientoId)}_TESORERIA_${tipoMov}`;
  const glosaBase = `${tipoMov === "COBRO" ? "Cobro" : "Pago"} tesorería ${cleanText(tercero)}`.trim();

  const descUpper = String(descripcion || "").toUpperCase();
  const catUpper = String(categoria || "").toUpperCase();
  const fullText = `${descUpper} ${catUpper}`;
  const medPagoUpper = String(medioPago || "").toUpperCase();
  const esEfectivo = medPagoUpper === "EFECTIVO" || medPagoUpper === "EFECTIVO EN CAJA";

  let cuentaDebe = esCobro ? (esEfectivo ? "101101" : "104101") : "421201";
  let descDebe = esCobro ? (esEfectivo ? "Caja efectivo" : "Bancos cuenta operativa") : "Cuentas por pagar comerciales";
  let cuentaHaber = esCobro ? "12121" : (esEfectivo ? "101101" : "104101");
  let descHaber = esCobro ? "Cuentas por cobrar comerciales" : (esEfectivo ? "Caja efectivo" : "Bancos cuenta operativa");

  // Specific rules for Planilla, ITF, AFP and Retiro de Socio payments
  if (!esCobro) {
    if (fullText.includes("AFP") || fullText.includes("PRIMA") || fullText.includes("INTEGRA") || fullText.includes("PROFUTURO") || fullText.includes("HABITAT")) {
      cuentaDebe = "403202";
      descDebe = "AFP por Pagar";
    } else if (fullText.includes("PLANILLA") || fullText.includes("SUELDO") || fullText.includes("HABERES")) {
      cuentaDebe = "411101";
      descDebe = "Sueldos y Salarios por Pagar";
    } else if (fullText.includes("ITF")) {
      cuentaDebe = "641201";
      descDebe = "Impuesto a las Transacciones Financieras";
    } else if (fullText.includes("RETIRO DE SOCIO") || fullText.includes("RETIRO SOCIO") || fullText.includes("OSCAR CRISTH") || fullText.includes("CARGO TRANSFERENCIA")) {
      cuentaDebe = "142101";
      descDebe = "Cuentas por cobrar a socios y accionistas";
    }
  }

  const payload = createAsientoPayload({
    rucEmpresa,
    origen: "TESORERIA",
    origenId: movimientoId,
    fecha,
    glosa: cleanText(descripcion) || glosaBase,
    moneda: cleanText(moneda || "PEN"),
    tipoCambio: cleanNumber(tipoCambio || 1),
    debe: [
      {
        cuenta: cuentaDebe,
        descripcion: descDebe,
        monto: m,
      },
    ],
    haber: [
      {
        cuenta: cuentaHaber,
        descripcion: descHaber,
        monto: m,
      },
    ],
  });

  const key = `${cleanText(rucEmpresa)}_TESORERIA_${cleanText(movimientoId)}_v1`;
  const auditMetadata = {
    sourceModule: "TESORERIA",
    sourceType: tipoMov,
    sourceId: movimientoId,
    sourceDocumentId: movimientoId,
  };

  if (transaction) {
    await registrarAsientoCore(transaction, {
      rucEmpresa,
      asientoId,
      payload,
      idempotencyKey: key,
      metadata: auditMetadata,
    });
  } else {
    await runTransaction(db, async (tx) => {
      await registrarAsientoCore(tx, {
        rucEmpresa,
        asientoId,
        payload,
        idempotencyKey: key,
        metadata: auditMetadata,
      });
    });
  }
  return asientoId;
};

export const registrarAsientoVenta = async ({
  rucEmpresa,
  ventaId,
  fecha,
  cliente,
  subtotal,
  igv,
  total,
  moneda,
  tipoCambio,
  idempotencyKey,
  metadata = {},
}, transaction) => {
  const cfg = await resolverCuentasContables(rucEmpresa);
  const montoSubtotal = cleanNumber(subtotal);
  const montoIgv = cleanNumber(igv);
  const montoTotal = cleanNumber(total);
  if (montoTotal <= 0) throw new Error("Total inválido para asiento de venta.");

  const asientoId = `${cleanText(ventaId)}_VENTA`;
  const payload = createAsientoPayload({
    rucEmpresa,
    origen: "VENTA",
    origenId: ventaId,
    fecha,
    glosa: `Venta ${cleanText(cliente)}`,
    moneda: cleanText(moneda || "PEN"),
    tipoCambio: cleanNumber(tipoCambio || 1),
    debe: [{ cuenta: cfg.cuenta_clientes, descripcion: "Cuentas por cobrar comerciales", monto: montoTotal }],
    haber: [
      { cuenta: cfg.cuenta_ventas, descripcion: "Ventas de servicios", monto: montoSubtotal || montoTotal },
      ...(montoIgv > 0 ? [{ cuenta: cfg.cuenta_igv, descripcion: "IGV por pagar", monto: montoIgv }] : []),
    ],
  });

  const key = idempotencyKey || `${cleanText(rucEmpresa)}_VENTA_${cleanText(ventaId)}_v1`;
  const auditMetadata = {
    sourceModule: "GESTION_VENTAS",
    sourceType: "COMPROBANTE_VENTA",
    sourceId: ventaId,
    sourceDocumentId: ventaId,
    ...metadata,
  };

  if (!transaction) {
    throw new Error("Transacción requerida: registrarAsientoVenta debe ejecutarse dentro de una transacción de Firestore.");
  }
  await registrarAsientoCore(transaction, {
    rucEmpresa,
    asientoId,
    payload,
    idempotencyKey: key,
    metadata: auditMetadata,
  });
  return asientoId;
};

export const registrarAsientoNotaCredito = async ({
  rucEmpresa,
  notaCreditoId,
  fecha,
  cliente,
  subtotal,
  igv,
  total,
  moneda,
  tipoCambio,
  idempotencyKey,
  metadata = {},
}, transaction) => {
  const cfg = await resolverCuentasContables(rucEmpresa);
  const montoSubtotal = cleanNumber(subtotal);
  const montoIgv = cleanNumber(igv);
  const montoTotal = cleanNumber(total);
  if (montoTotal <= 0) throw new Error("Total inválido para asiento de nota de crédito.");

  const asientoId = `${cleanText(notaCreditoId)}_NC`;
  
  // En nota de crédito revertimos los ingresos e IGV al Debe, y abonamos la Cuentas por Cobrar al Haber
  const payload = createAsientoPayload({
    rucEmpresa,
    origen: "NOTA_CREDITO",
    origenId: notaCreditoId,
    fecha,
    glosa: `Nota de Crédito ${cleanText(cliente)}`,
    moneda: cleanText(moneda || "PEN"),
    tipoCambio: cleanNumber(tipoCambio || 1),
    debe: [
      { cuenta: cfg.cuenta_ventas, descripcion: "Reversión ventas de servicios", monto: montoSubtotal || montoTotal },
      ...(montoIgv > 0 ? [{ cuenta: cfg.cuenta_igv, descripcion: "Reversión IGV por pagar", monto: montoIgv }] : []),
    ],
    haber: [{ cuenta: cfg.cuenta_clientes, descripcion: "Cuentas por cobrar comerciales (Reversión)", monto: montoTotal }],
  });

  const key = idempotencyKey || `${cleanText(rucEmpresa)}_NC_${cleanText(notaCreditoId)}_v1`;
  const auditMetadata = {
    sourceModule: "GESTION_VENTAS",
    sourceType: "NOTA_CREDITO",
    sourceId: notaCreditoId,
    sourceDocumentId: notaCreditoId,
    ...metadata,
  };

  if (!transaction) {
    throw new Error("Transacción requerida: registrarAsientoNotaCredito debe ejecutarse dentro de una transacción de Firestore.");
  }
  await registrarAsientoCore(transaction, {
    rucEmpresa,
    asientoId,
    payload,
    idempotencyKey: key,
    metadata: auditMetadata,
  });
  return asientoId;
};

export const registrarAsientoNotaCreditoCompra = async ({
  rucEmpresa,
  notaCreditoId,
  fecha,
  proveedorRazonSocial,
  proveedorRuc,
  subtotal,
  igv,
  total,
  moneda,
  tipoCambio,
  tipoGasto = "GASTO_GENERAL",
  idempotencyKey,
  metadata = {},
}, transaction) => {
  const cfg = await resolverCuentasContables(rucEmpresa);
  const regla = await getReglaTipoGasto(rucEmpresa, tipoGasto);
  const montoSubtotal = cleanNumber(subtotal);
  const montoIgv = cleanNumber(igv);
  const montoTotal = cleanNumber(total);
  if (montoTotal <= 0) throw new Error("Total inválido para asiento de nota de crédito de compra.");

  const asientoId = `${cleanText(notaCreditoId)}_NC_COMPRA`;
  
  const payload = createAsientoPayload({
    rucEmpresa,
    origen: "NOTA_CREDITO_COMPRA",
    origenId: notaCreditoId,
    fecha,
    glosa: `Nota Crédito Compra ${cleanText(proveedorRazonSocial)}`,
    moneda: cleanText(moneda || "PEN"),
    tipoCambio: cleanNumber(tipoCambio || 1),
    debe: [{ cuenta: cfg.cuenta_proveedores, descripcion: "Proveedores por pagar (Reversión)", monto: montoTotal }],
    haber: [
      { cuenta: cleanText(regla?.cuenta_debe || "659101"), descripcion: `Reversión gasto por ${cleanText(tipoGasto)}`, monto: montoSubtotal || montoTotal },
      ...(montoIgv > 0 ? [{ cuenta: cfg.cuenta_igv, descripcion: "Reversión IGV crédito fiscal", monto: montoIgv }] : []),
    ],
  });

  const key = idempotencyKey || `${cleanText(rucEmpresa)}_NC_COMPRA_${cleanText(notaCreditoId)}_v1`;
  const auditMetadata = {
    sourceModule: "GESTION_COMPRAS",
    sourceType: "NOTA_CREDITO_COMPRA",
    sourceId: notaCreditoId,
    sourceDocumentId: notaCreditoId,
    ...metadata,
  };

  if (!transaction) {
    throw new Error("Transacción requerida: registrarAsientoNotaCreditoCompra debe ejecutarse dentro de una transacción de Firestore.");
  }
  await registrarAsientoCore(transaction, {
    rucEmpresa,
    asientoId,
    payload,
    idempotencyKey: key,
    metadata: auditMetadata,
  });
  return asientoId;
};

const inferirCuentaDestino = (linea = {}, asiento = {}) => {
  const cc = cleanText(linea.centro_costo || asiento.centro_costo || "").toUpperCase();
  if (cc.includes("VENTA")) return DESTINO_CUENTAS.VENTAS;
  if (cc.includes("PRODU") || cc.includes("OPERA")) return DESTINO_CUENTAS.OPERATIVO;
  if (cc.includes("ADMIN")) return DESTINO_CUENTAS.ADMINISTRATIVO;
  return DESTINO_CUENTAS.ADMINISTRATIVO;
};

const buildAsientoDestinoDesdePlanilla = ({ rucEmpresa, origenId, fecha, glosa, lineasGasto }) => {
  const agrupado = {};
  lineasGasto.forEach((l) => {
    const destino = inferirCuentaDestino(l, {});
    agrupado[destino] = (agrupado[destino] || 0) + cleanNumber(l.monto);
  });
  const debe = Object.entries(agrupado).map(([cuenta, monto]) => ({
    cuenta,
    descripcion: "Asignación por destino",
    monto: Number(monto.toFixed(2)),
  }));
  const total = debe.reduce((acc, x) => acc + cleanNumber(x.monto), 0);
  const haber = total > 0
    ? [{ cuenta: "791101", descripcion: "Cargas imputables a cuentas de costos", monto: Number(total.toFixed(2)) }]
    : [];

  return createAsientoPayload({
    rucEmpresa,
    origen: "PLANILLA_DESTINO",
    origenId,
    fecha,
    glosa: `${cleanText(glosa)} - Destino`,
    moneda: "PEN",
    tipoCambio: 1,
    debe,
    haber,
  });
};

export const generarAsientoProvisionesPlanilla = async ({ rucEmpresa, planillaId }, transaction) => {
  const execute = async (tx) => {
    const planillaRef = doc(db, "pl_planillas", cleanText(planillaId));
    const planillaSnap = await tx.get(planillaRef);
    if (!planillaSnap.exists()) return null;
    const planilla = planillaSnap.data();

    const detQ = query(collection(db, "pl_planilla_detalle"), where("planilla_id", "==", cleanText(planillaId)), limit(1000));
    const detSnap = await getDocs(detQ);
    const detalles = detSnap.docs.map((d) => d.data());
    if (detalles.length === 0) return null;

    const totalProvGrati = detalles.reduce((sum, d) => sum + cleanNumber(d.provisiones?.gratificaciones), 0);
    const totalProvVac = detalles.reduce((sum, d) => sum + cleanNumber(d.provisiones?.vacaciones), 0);
    const totalProvCTS = detalles.reduce((sum, d) => sum + cleanNumber(d.provisiones?.cts), 0);
    const totalProvEssaludVac = detalles.reduce((sum, d) => sum + cleanNumber(d.provisiones?.essalud_vacaciones), 0);
    const totalProvBono = detalles.reduce((sum, d) => sum + cleanNumber(d.provisiones?.bono_extraordinario), 0);

    const debe = [];
    const haber = [];
    if (totalProvGrati > 0) {
      debe.push({ cuenta: "621501", descripcion: "Provisión gratificaciones", monto: Number(totalProvGrati.toFixed(2)) });
      haber.push({ cuenta: "411201", descripcion: "Gratificaciones por pagar", monto: Number(totalProvGrati.toFixed(2)) });
    }
    if (totalProvVac > 0) {
      debe.push({ cuenta: "621401", descripcion: "Provisión vacaciones", monto: Number(totalProvVac.toFixed(2)) });
      haber.push({ cuenta: "411301", descripcion: "Vacaciones por pagar", monto: Number(totalProvVac.toFixed(2)) });
    }
    if (totalProvCTS > 0) {
      debe.push({ cuenta: "621201", descripcion: "Provisión CTS", monto: Number(totalProvCTS.toFixed(2)) });
      haber.push({ cuenta: "411401", descripcion: "CTS por pagar", monto: Number(totalProvCTS.toFixed(2)) });
    }
    if (totalProvEssaludVac > 0) {
      debe.push({ cuenta: "627101", descripcion: "ESSALUD provisión vacaciones", monto: Number(totalProvEssaludVac.toFixed(2)) });
      haber.push({ cuenta: "403101", descripcion: "ESSALUD por pagar", monto: Number(totalProvEssaludVac.toFixed(2)) });
    }
    if (totalProvBono > 0) {
      debe.push({ cuenta: "622101", descripcion: "Bonificación extraordinaria", monto: Number(totalProvBono.toFixed(2)) });
      haber.push({ cuenta: "411501", descripcion: "Bonificaciones por pagar", monto: Number(totalProvBono.toFixed(2)) });
    }
    if (debe.length === 0 || haber.length === 0) return null;

    const periodo = cleanText(planilla.periodo || "");
    const payload = createAsientoPayload({
      rucEmpresa: cleanText(rucEmpresa || planilla.ruc_empresa),
      origen: "PLANILLA_PROVISION",
      origenId: cleanText(planillaId),
      fecha: periodo ? `${periodo}-01` : "",
      glosa: `Provisión mensual planilla ${periodo}`,
      moneda: "PEN",
      tipoCambio: 1,
      debe,
      haber,
    });
    const asientoId = `PLANILLA_${cleanText(planillaId)}_PROVISIONES`;
    const key = `${cleanText(rucEmpresa)}_PLANILLA_${cleanText(planillaId)}_PROV_v1`;
    await registrarAsientoCore(tx, {
      rucEmpresa: cleanText(rucEmpresa || planilla.ruc_empresa),
      asientoId,
      payload,
      idempotencyKey: key,
      metadata: {
        sourceModule: "PLANILLAS",
        sourceType: "PLANILLA_PROVISION",
        sourceId: planillaId,
      },
    });
    return asientoId;
  };

  if (transaction) {
    return execute(transaction);
  }
  return runTransaction(db, execute);
};

export const sincronizarAsientosPlanilla = async (rucEmpresa) => {
  const q = query(
    collection(db, "pl_asientos_contables"),
    where("ruc_empresa", "==", cleanText(rucEmpresa)),
    limit(200)
  );
  const snap = await getDocs(q);
  let synced = 0;
  const tasks = snap.docs.map(async (d) => {
    const p = d.data();
    const asientoId = `PLANILLA_${d.id}`;
    const debe = (p.debe || []).map((x) => ({
      cuenta: cleanText(x.cuenta),
      descripcion: cleanText(x.descripcion_cuenta || x.descripcion),
      monto: cleanNumber(x.monto),
    }));
    const haber = (p.haber || []).map((x) => ({
      cuenta: cleanText(x.cuenta),
      descripcion: cleanText(x.descripcion_cuenta || x.descripcion),
      monto: cleanNumber(x.monto),
    }));
    const payload = createAsientoPayload({
      rucEmpresa: cleanText(p.ruc_empresa || rucEmpresa),
      origen: "PLANILLA",
      origenId: cleanText(p.planilla_id || d.id),
      fecha: p.periodo ? `${p.periodo}-01` : "",
      glosa: cleanText(p.glosa || `Asiento planilla ${p.periodo || ""}`),
      moneda: "PEN",
      tipoCambio: 1,
      debe,
      haber,
    });
    await setDoc(doc(db, CONT_COLLECTIONS.ASIENTOS, asientoId), payload, { merge: true });

    const lineasGasto = (debe || []).filter((x) => String(x.cuenta || "").startsWith("6"));
    if (lineasGasto.length > 0) {
      const destinoPayload = buildAsientoDestinoDesdePlanilla({
        rucEmpresa: cleanText(p.ruc_empresa || rucEmpresa),
        origenId: cleanText(p.planilla_id || d.id),
        fecha: p.periodo ? `${p.periodo}-01` : "",
        glosa: cleanText(p.glosa || `Asiento planilla ${p.periodo || ""}`),
        lineasGasto,
      });
      await setDoc(doc(db, CONT_COLLECTIONS.ASIENTOS, `${asientoId}_DESTINO`), destinoPayload, { merge: true });
    }

    const planillaId = cleanText(p.planilla_id || "");
    if (planillaId) {
      await generarAsientoProvisionesPlanilla({
        rucEmpresa: cleanText(p.ruc_empresa || rucEmpresa),
        planillaId,
      });
    }
    synced += 1;
  });
  await Promise.all(tasks);
  return synced;
};

export const sincronizarAsientosPlanillaPorPlanilla = async ({ rucEmpresa, planillaId, estadoPlanilla = "PROCESADO" }) => {
  const ruc = cleanText(rucEmpresa);
  const idPlanilla = cleanText(planillaId);
  if (!ruc || !idPlanilla) return 0;

  const q = query(
    collection(db, "pl_asientos_contables"),
    where("ruc_empresa", "==", ruc),
    where("planilla_id", "==", idPlanilla),
    limit(200)
  );
  const snap = await getDocs(q);
  let synced = 0;

  const tasks = snap.docs.map(async (d) => {
    const p = d.data();
    const asientoId = `PLANILLA_${d.id}`;
    const debe = (p.debe || []).map((x) => ({
      cuenta: cleanText(x.cuenta),
      descripcion: cleanText(x.descripcion_cuenta || x.descripcion),
      monto: cleanNumber(x.monto),
      centro_costo: cleanText(x.centro_costo),
    }));
    const haber = (p.haber || []).map((x) => ({
      cuenta: cleanText(x.cuenta),
      descripcion: cleanText(x.descripcion_cuenta || x.descripcion),
      monto: cleanNumber(x.monto),
      centro_costo: cleanText(x.centro_costo),
    }));
    const payload = {
      ...createAsientoPayload({
        rucEmpresa: cleanText(p.ruc_empresa || ruc),
        origen: "PLANILLA",
        origenId: idPlanilla,
        fecha: p.periodo ? `${p.periodo}-01` : "",
        glosa: cleanText(p.glosa || `Asiento planilla ${p.periodo || ""}`),
        moneda: "PEN",
        tipoCambio: 1,
        debe,
        haber,
      }),
      planilla_id: idPlanilla,
      periodo: cleanText(p.periodo),
      estado_planilla: cleanText(estadoPlanilla || "PROCESADO"),
    };
    await setDoc(doc(db, CONT_COLLECTIONS.ASIENTOS, asientoId), payload, { merge: true });

    const lineasGasto = (debe || []).filter((x) => String(x.cuenta || "").startsWith("6"));
    if (lineasGasto.length > 0) {
      const destinoPayload = {
        ...buildAsientoDestinoDesdePlanilla({
          rucEmpresa: cleanText(p.ruc_empresa || ruc),
          origenId: idPlanilla,
          fecha: p.periodo ? `${p.periodo}-01` : "",
          glosa: cleanText(p.glosa || `Asiento planilla ${p.periodo || ""}`),
          lineasGasto,
        }),
        planilla_id: idPlanilla,
        periodo: cleanText(p.periodo),
        estado_planilla: cleanText(estadoPlanilla || "PROCESADO"),
      };
      await setDoc(doc(db, CONT_COLLECTIONS.ASIENTOS, `${asientoId}_DESTINO`), destinoPayload, { merge: true });
    }
    synced += 1;
  });

  await Promise.all(tasks);
  await generarAsientoProvisionesPlanilla({ rucEmpresa: ruc, planillaId: idPlanilla });
  await actualizarEstadoPlanillaEnAsientosContables({ rucEmpresa: ruc, planillaId: idPlanilla, estadoPlanilla });
  return synced;
};

export const actualizarEstadoPlanillaEnAsientosContables = async ({ rucEmpresa, planillaId, estadoPlanilla }) => {
  const ruc = cleanText(rucEmpresa);
  const idPlanilla = cleanText(planillaId);
  if (!ruc || !idPlanilla) return 0;

  const q = query(
    collection(db, CONT_COLLECTIONS.ASIENTOS),
    where("ruc_empresa", "==", ruc),
    where("origen_id", "==", idPlanilla),
    limit(500)
  );
  const snap = await getDocs(q);
  await Promise.all(
    snap.docs.map((d) =>
      updateDoc(doc(db, CONT_COLLECTIONS.ASIENTOS, d.id), {
        estado_planilla: cleanText(estadoPlanilla || ""),
        updated_at: serverTimestamp(),
      })
    )
  );
  return snap.docs.length;
};

export const obtenerAsientosPorEmpresa = async (rucEmpresa) => {
  const q = query(collection(db, CONT_COLLECTIONS.ASIENTOS), where("ruc_empresa", "==", cleanText(rucEmpresa)));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.fecha || "").localeCompare(String(a.fecha || "")));
};

export const obtenerLibroDiarioPorEmpresa = async (rucEmpresa) => obtenerAsientosPorEmpresa(rucEmpresa);

export const obtenerLibroMayorPorEmpresa = async (rucEmpresa) => {
  const asientos = await obtenerAsientosPorEmpresa(rucEmpresa);
  const mayor = {};

  asientos.forEach((asiento) => {
    (asiento.debe || []).forEach((linea) => {
      const cuenta = cleanText(linea.cuenta);
      if (!cuenta) return;
      if (!mayor[cuenta]) {
        mayor[cuenta] = { cuenta, descripcion: cleanText(linea.descripcion), debe: 0, haber: 0, saldo: 0 };
      }
      mayor[cuenta].debe += cleanNumber(linea.monto);
    });

    (asiento.haber || []).forEach((linea) => {
      const cuenta = cleanText(linea.cuenta);
      if (!cuenta) return;
      if (!mayor[cuenta]) {
        mayor[cuenta] = { cuenta, descripcion: cleanText(linea.descripcion), debe: 0, haber: 0, saldo: 0 };
      }
      mayor[cuenta].haber += cleanNumber(linea.monto);
    });
  });

  return Object.values(mayor)
    .map((item) => ({
      ...item,
      debe: Number(item.debe.toFixed(2)),
      haber: Number(item.haber.toFixed(2)),
      saldo: Number((item.debe - item.haber).toFixed(2)),
    }))
    .sort((a, b) => a.cuenta.localeCompare(b.cuenta));
};

export const obtenerAsientoPorId = async ({ rucEmpresa, asientoId }) => {
  const ruc = cleanText(rucEmpresa);
  const id = cleanText(asientoId);
  if (!ruc || !id) return null;

  const ref = doc(db, CONT_COLLECTIONS.ASIENTOS, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;

  const data = snap.data() || {};
  if (cleanText(data.ruc_empresa) !== ruc) return null;
  return { id: snap.id, ...data };
};

export const generarLibroDiario = async ({ rucEmpresa, periodo }) => {
  const ruc = cleanText(rucEmpresa);
  const per = cleanText(periodo);
  if (!ruc || !per) throw new Error("RUC y periodo son obligatorios para generar el Libro Diario.");

  const q = query(
    collection(db, CONT_COLLECTIONS.ASIENTOS),
    where("ruc_empresa", "==", ruc),
    where("periodo", "==", per)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const cmpFecha = String(a.fecha || "").localeCompare(String(b.fecha || ""));
      if (cmpFecha !== 0) return cmpFecha;
      return String(a.cuo || "").localeCompare(String(b.cuo || ""));
    });
};

export const generarLibroMayor = async ({ rucEmpresa, periodo }) => {
  const asientos = await generarLibroDiario({ rucEmpresa, periodo });
  const mayor = {};

  asientos.forEach((asiento) => {
    (asiento.debe || []).forEach((linea) => {
      const cuenta = cleanText(linea.cuenta);
      if (!cuenta) return;
      if (!mayor[cuenta]) {
        mayor[cuenta] = { cuenta, descripcion: cleanText(linea.descripcion), debe: 0, haber: 0, saldo: 0 };
      }
      mayor[cuenta].debe += cleanNumber(linea.monto);
    });

    (asiento.haber || []).forEach((linea) => {
      const cuenta = cleanText(linea.cuenta);
      if (!cuenta) return;
      if (!mayor[cuenta]) {
        mayor[cuenta] = { cuenta, descripcion: cleanText(linea.descripcion), debe: 0, haber: 0, saldo: 0 };
      }
      mayor[cuenta].haber += cleanNumber(linea.monto);
    });
  });

  return Object.values(mayor)
    .map((item) => {
      const debe = Number(item.debe.toFixed(2));
      const haber = Number(item.haber.toFixed(2));
      const firstDigit = item.cuenta.charAt(0);
      const esAcreedora = ["4", "5", "7"].includes(firstDigit);
      const saldoRaw = esAcreedora ? (haber - debe) : (debe - haber);
      return {
        ...item,
        debe,
        haber,
        saldo: Number(saldoRaw.toFixed(2)),
      };
    })
    .sort((a, b) => a.cuenta.localeCompare(b.cuenta));
};

export const obtenerMovimientosMayorPorCuenta = async ({ rucEmpresa, cuentaCodigo, periodo }) => {
  const asientos = await generarLibroDiario({ rucEmpresa, periodo });
  const targetCuenta = cleanText(cuentaCodigo);
  if (!targetCuenta) throw new Error("Código de cuenta es obligatorio.");

  const movimientos = [];
  asientos.forEach((asiento) => {
    (asiento.debe || []).forEach((linea) => {
      if (cleanText(linea.cuenta) === targetCuenta) {
        movimientos.push({
          fecha: asiento.fecha,
          cuo: asiento.cuo,
          glosa: asiento.glosa || linea.descripcion,
          debe: cleanNumber(linea.monto),
          haber: 0,
        });
      }
    });

    (asiento.haber || []).forEach((linea) => {
      if (cleanText(linea.cuenta) === targetCuenta) {
        movimientos.push({
          fecha: asiento.fecha,
          cuo: asiento.cuo,
          glosa: asiento.glosa || linea.descripcion,
          debe: 0,
          haber: cleanNumber(linea.monto),
        });
      }
    });
  });

  movimientos.sort((a, b) => {
    const cmpFecha = String(a.fecha || "").localeCompare(String(b.fecha || ""));
    if (cmpFecha !== 0) return cmpFecha;
    return String(a.cuo || "").localeCompare(String(b.cuo || ""));
  });

  const firstDigit = targetCuenta.charAt(0);
  const esAcreedora = ["4", "5", "7"].includes(firstDigit);
  let saldoAcumulado = 0;

  return movimientos.map((mov) => {
    const mDebe = cleanNumber(mov.debe);
    const mHaber = cleanNumber(mov.haber);
    if (esAcreedora) {
      saldoAcumulado += (mHaber - mDebe);
    } else {
      saldoAcumulado += (mDebe - mHaber);
    }
    return {
      ...mov,
      saldo: Number(saldoAcumulado.toFixed(2)),
    };
  });
};
